/**
 * Display formatting for outcome market data.
 *
 * Outcome prices are probabilities in (0, 1) with at most 5 decimals, so we show them as cents
 * (0.63215 → "63.215¢") and keep the raw wire price for tooltips. Price math is done in integer
 * units of 1e-5 to avoid float noise (0.67 − 0.61 must print as 6¢, not 6.000000000000005¢).
 */
import { cents, numberFormat, usd } from '../i18n/format';
import { getLocale } from '../i18n/i18n';

/** Integer price units: 1 unit = 0.00001 (the outcome tick). */
export const PX_UNITS_PER_ONE = 100_000;

/** Wire price string → integer units of 1e-5 (NaN-safe: returns NaN for junk). */
export function pxToUnits(px: string | number): number {
  const v = typeof px === 'number' ? px : Number(px);
  return Math.round(v * PX_UNITS_PER_ONE);
}

function stripZeros(s: string): string {
  return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s;
}

/** Price units (may be fractional, e.g. a mid of two ticks) → "63.2¢". */
export function unitsToCents(units: number): string {
  if (!Number.isFinite(units)) return '—';
  // 1¢ = 1000 units; 4 decimals cover half-tick mids exactly.
  return cents(stripZeros((units / 1000).toFixed(4)));
}

/** Wire price → cents label, e.g. "0.0861" → "8.61¢". */
export function pxToCents(px: string | number): string {
  return unitsToCents(pxToUnits(px));
}

/** USDC amount ("6.35") → "$6.35" (`digits` decimals); "—" for junk. Shared by the order ticket and /learn. */
export function formatUsd(v: string | number, digits = 2): string {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? usd(n.toFixed(digits)) : '—';
}

const sharesFmts = new Map<string, Intl.NumberFormat>();

/** Share count ("4732.0") → "4,732" (grouping per locale: pt-BR "4.732"). */
export function formatShares(sz: string | number): string {
  const v = typeof sz === 'number' ? sz : Number(sz);
  if (!Number.isFinite(v)) return '—';
  const loc = getLocale();
  let f = sharesFmts.get(loc);
  if (!f) sharesFmts.set(loc, (f = numberFormat({ maximumFractionDigits: 2 })));
  return f.format(v);
}

/** ms epoch → local "HH:MM:SS". */
export function formatTime(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}
